import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Briefcase, 
  Mail, 
  ArrowRight, 
  Lock, 
  Loader2,
  Copy,
  Check
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface ComingSoonProps {
  onAccessGranted: () => void;
}

const ACCESS_CODE = "shakatah2025";

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
export function ComingSoon({ onAccessGranted }: ComingSoonProps) {
  const [email, setEmail] = useState("");
  const [accessCode, setAccessCode] = useState("");
  const [showAccessInput, setShowAccessInput] = useState(false);
  const [error, setError] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  const inviteLink = `${window.location.origin}?access=${ACCESS_CODE}`;

  const copyInviteLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopiedLink(true);
    toast.success("Invite link copied!");
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleAccessSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (accessCode.toLowerCase() === ACCESS_CODE.toLowerCase()) {
      localStorage.setItem("shakatah_access", "granted");
      onAccessGranted();
    } else {
      setError("Invalid access code");
      setTimeout(() => setError(""), 3000);
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubmitting(true);
    const trimmedEmail = email.trim().toLowerCase();
    
    try {
      const { error } = await supabase
        .from('waitlist_signups')
        .insert({ email: trimmedEmail });
      
      if (error) {
        if (error.code === '23505') {
          toast.info("You're already on the waitlist!");
          setSubscribed(true);
          setEmail("");
          return;
        } else {
          throw error;
        }
      }
      
      // Send confirmation email
      try {
        await supabase.functions.invoke('send-waitlist-confirmation', {
          body: { email: trimmedEmail }
        });
      } catch (emailErr) {
        console.error("Failed to send confirmation email:", emailErr);
      }
      
      toast.success("Thanks! Check your inbox for a confirmation.");
      setSubscribed(true);
      setEmail("");
    } catch (err) {
      console.error("Waitlist signup error:", err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-muted/30 flex flex-col">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-cyan-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-teal-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-r from-cyan-500 to-teal-500 text-white mb-6 shadow-lg shadow-cyan-500/25">
            <Briefcase className="w-8 h-8" />
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-foreground mb-4 tracking-tight">
            Shakatah for Pros
          </h1>
          <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Enabling you to focus on your profession
          </p>
          
          {/* Stealth Mode Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-muted border border-muted-foreground/20 text-muted-foreground text-sm font-medium">
            <Lock className="w-4 h-4" />
            Early Access Only
          </div>

        </div>

        {/* Access Options */}
        <div className="w-full max-w-2xl space-y-8">
          {/* Code/Link Access */}
          <div className="space-y-4">
            <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Get Access</p>
            
            {!showAccessInput ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  onClick={() => setShowAccessInput(true)}
                  className="flex items-center justify-center gap-2 px-6 py-4 rounded-lg border border-primary/30 bg-primary/5 hover:bg-primary/10 text-primary font-medium transition-colors"
                >
                  <Lock className="w-4 h-4" />
                  Enter Code
                </button>
                <button
                  onClick={copyInviteLink}
                  className="flex items-center justify-center gap-2 px-6 py-4 rounded-lg border border-primary/30 bg-primary/5 hover:bg-primary/10 text-primary font-medium transition-colors"
                >
                  {copiedLink ? (
                    <>
                      <Check className="w-4 h-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copy Link
                    </>
                  )}
                </button>
              </div>
            ) : (
              <form onSubmit={handleAccessSubmit} className="space-y-3">
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="password"
                    placeholder="Enter access code"
                    value={accessCode}
                    onChange={(e) => setAccessCode(e.target.value)}
                    className="pl-10 h-12"
                    autoFocus
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" size="lg" className="flex-1 h-12">
                    <ArrowRight className="mr-2 w-4 h-4" />
                    Enter
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="lg" 
                    className="h-12"
                    onClick={() => {
                      setShowAccessInput(false);
                      setError("");
                      setAccessCode("");
                    }}
                  >
                    Cancel
                  </Button>
                </div>
                {error && (
                  <p className="text-destructive text-sm text-center font-medium">{error}</p>
                )}
              </form>
            )}
          </div>

          {/* Waitlist */}
          <div className="border-t border-muted pt-8 space-y-4">
            <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Be Notified</p>
            
            {!subscribed ? (
              <form onSubmit={handleEmailSubmit} className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-12"
                    required
                  />
                </div>
                <Button type="submit" size="lg" className="h-12 whitespace-nowrap" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      Notify Me
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </>
                  )}
                </Button>
              </form>
            ) : (
              <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                <p className="text-primary font-medium">
                  ✓ You're on the waitlist! We'll send you updates.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

    </div>
  );
}
